<!--
  Screenshot Share Dialog
  =======================
  Opened via NgDialogAnimationService.open(ScreenshotShareDialogComponent, ...).
  Single shareForm reactive FormGroup drives every input.
-->
<div class="ss-share" [formGroup]="shareForm" @fadeScale>
  <!-- Success screen -->
  <ng-container *ngIf="successScreen; else mainScreen">
    <div class="ss-share-success">
      <div class="ss-share-success-check" aria-hidden="true">
        <mat-icon>check</mat-icon>
      </div>
      <h3 class="ss-share-success-title">
        {{ lastShareResult?.recipients?.length ? 'Sent to ' + lastShareResult?.recipients?.length + ' people' : 'Share link ready' }}
      </h3>
      <div class="ss-share-success-chips" *ngIf="lastShareResult?.recipients?.length">
        <span class="ss-share-chip" *ngFor="let r of lastShareResult?.recipients">{{ r.name }}</span>
      </div>
      <div class="ss-share-link-box">
        <input type="text" readonly [value]="lastShareResult?.shareUrl || ''" aria-label="Share link" />
        <button type="button" class="ss-share-icon-btn" matTooltip="Copy link" (click)="copyLink()">
          <mat-icon>{{ copyFlash ? 'check' : 'content_copy' }}</mat-icon>
        </button>
      </div>
      <div class="ss-share-password-box" *ngIf="lastShareResult?.password">
        <span class="ss-share-password-label">Password</span>
        <code class="ss-share-password-code">{{ lastShareResult?.password }}</code>
        <button type="button" class="ss-share-icon-btn" matTooltip="Copy password" (click)="copyPassword()">
          <mat-icon>content_copy</mat-icon>
        </button>
      </div>
      <div class="ss-share-success-actions">
        <button type="button" class="ss-share-btn ss-share-btn-ghost" (click)="shareAgain()">Share again</button>
        <button type="button" class="ss-share-btn ss-share-btn-primary" (click)="done()">Done</button>
      </div>
    </div>
  </ng-container>

  <ng-template #mainScreen>
    <!-- Header -->
    <header class="ss-share-header">
      <div class="ss-share-header-title">
        <mat-icon aria-hidden="true">share</mat-icon>
        <h3>{{ titleText }}</h3>
      </div>
      <button type="button" class="ss-share-icon-btn" matTooltip="Close" aria-label="Close" (click)="close()">
        <mat-icon>close</mat-icon>
      </button>
    </header>

    <!-- Thumbnail strip -->
    <div class="ss-share-thumbs" *ngIf="thumbnailStrip.length > 0">
      <div class="ss-share-thumb" *ngFor="let s of thumbnailStrip" [matTooltip]="s.caption || s.pageName">
        <img [src]="s.thumbnail" [alt]="s.caption || 'Screenshot'" />
        <span class="ss-share-thumb-annot-badge" *ngIf="(s.annotations?.length ?? 0) > 0">{{ s.annotations.length }}</span>
      </div>
      <span class="ss-share-thumb-more" *ngIf="screenshots.length > 6">+{{ screenshots.length - 6 }}</span>
    </div>

    <!-- Tabs -->
    <div class="ss-share-tabs" role="tablist">
      <button
        type="button"
        class="ss-share-tab"
        [class.ss-share-tab-active]="activeTab === 'share'"
        role="tab"
        (click)="switchTab('share')"
      >
        <mat-icon>send</mat-icon>
        <span>Share</span>
      </button>
      <button
        type="button"
        class="ss-share-tab"
        [class.ss-share-tab-active]="activeTab === 'activity'"
        role="tab"
        (click)="switchTab('activity')"
      >
        <mat-icon>schedule</mat-icon>
        <span>Activity</span>
        <span class="ss-share-tab-badge" *ngIf="activityCount > 0">{{ activityCount }}</span>
      </button>
    </div>

    <!-- Share tab -->
    <div class="ss-share-body" *ngIf="activeTab === 'share'">
      <!-- Quick-send buttons -->
      <section class="ss-share-section">
        <p class="ss-share-section-label">Quick send</p>
        <div class="ss-share-quicksend">
          <button type="button" class="ss-share-quicksend-btn" (click)="copyLink()">
            <mat-icon>link</mat-icon>
            <span>Copy link</span>
          </button>
          <button type="button" class="ss-share-quicksend-btn" disabled matTooltip="Coming soon">
            <mat-icon>image</mat-icon>
            <span>Save PNG</span>
          </button>
          <button type="button" class="ss-share-quicksend-btn" disabled matTooltip="Coming soon">
            <mat-icon>picture_as_pdf</mat-icon>
            <span>Save PDF</span>
          </button>
          <button type="button" class="ss-share-quicksend-btn" disabled matTooltip="Coming soon">
            <mat-icon>print</mat-icon>
            <span>Print</span>
          </button>
          <button type="button" class="ss-share-quicksend-btn" disabled matTooltip="Coming soon">
            <mat-icon>forum</mat-icon>
            <span>Slack</span>
          </button>
        </div>
      </section>

      <!-- Recipients -->
      <section class="ss-share-section">
        <p class="ss-share-section-label">Send to people</p>
        <div class="ss-share-recipient-field">
          <span class="ss-share-chip" *ngFor="let r of shareForm.controls.recipients.value; trackBy: trackByContact">
            <span class="ss-share-chip-name">{{ r.name }}</span>
            <button type="button" class="ss-share-chip-remove" aria-label="Remove recipient" (click)="removeRecipient(r)">
              <mat-icon>close</mat-icon>
            </button>
          </span>
          <input
            type="text"
            class="ss-share-recipient-input"
            formControlName="recipientSearch"
            placeholder="Add name or email…"
            aria-label="Search contacts"
          />
        </div>
        <ul class="ss-share-suggestions" *ngIf="contactSuggestions.length > 0">
          <li *ngFor="let c of contactSuggestions; trackBy: trackByContact" class="ss-share-suggestion" (click)="addRecipient(c)">
            <span class="ss-share-avatar" [style.background]="c.color || '#6B7280'">{{ c.name.charAt(0) }}</span>
            <span class="ss-share-suggestion-main">
              <span class="ss-share-suggestion-name">{{ c.name }}</span>
              <span class="ss-share-suggestion-meta">{{ c.role }}{{ c.company ? ' · ' + c.company : '' }}</span>
            </span>
            <span class="ss-share-suggestion-email">{{ c.email }}</span>
          </li>
        </ul>
        <textarea
          class="ss-share-message"
          formControlName="message"
          rows="2"
          placeholder="Add a note (optional)"
          aria-label="Share message"
        ></textarea>
        <label class="ss-share-toggle">
          <input type="checkbox" formControlName="requireResponse" />
          <span>Request a response</span>
        </label>
      </section>

      <!-- Link & Access -->
      <section class="ss-share-section ss-share-section-card">
        <p class="ss-share-section-label">Link & access</p>
        <div class="ss-share-link-box">
          <input type="text" readonly [value]="previewShareUrl" aria-label="Share link preview" />
          <button type="button" class="ss-share-icon-btn" matTooltip="Copy link" (click)="copyLink()">
            <mat-icon>{{ copyFlash ? 'check' : 'content_copy' }}</mat-icon>
          </button>
        </div>

        <div class="ss-share-access-grid">
          <button
            type="button"
            class="ss-share-access-card"
            *ngFor="let opt of accessOptions"
            [class.ss-share-access-card-active]="shareForm.controls.access.value === opt.value"
            (click)="setAccess(opt.value)"
          >
            <mat-icon>{{ opt.icon }}</mat-icon>
            <span class="ss-share-access-name">{{ opt.label }}</span>
            <span class="ss-share-access-desc">{{ opt.description }}</span>
          </button>
        </div>

        <div class="ss-share-password-box" *ngIf="shareForm.controls.access.value === 'password'">
          <span class="ss-share-password-label">Password</span>
          <code class="ss-share-password-code">{{ shareForm.controls.password.value }}</code>
          <button type="button" class="ss-share-icon-btn" matTooltip="Regenerate" (click)="regeneratePassword()">
            <mat-icon>refresh</mat-icon>
          </button>
          <button type="button" class="ss-share-icon-btn" matTooltip="Copy password" (click)="copyPassword()">
            <mat-icon>content_copy</mat-icon>
          </button>
        </div>

        <div class="ss-share-row">
          <label class="ss-share-field">
            <span class="ss-share-field-label">Expires</span>
            <select formControlName="expiration" class="ss-share-field-input">
              <option *ngFor="let e of expirationOptions" [ngValue]="e.value">{{ e.label }}</option>
            </select>
          </label>
          <label class="ss-share-toggle">
            <input type="checkbox" formControlName="allowDownload" />
            <span>Allow download</span>
          </label>
          <label class="ss-share-toggle">
            <input type="checkbox" formControlName="allowComments" />
            <span>Allow comments</span>
          </label>
        </div>
      </section>

      <!-- Include in share -->
      <section class="ss-share-section">
        <p class="ss-share-section-label">Include in share</p>
        <label class="ss-share-toggle">
          <input type="checkbox" formControlName="includeAnnotations" />
          <span>Annotations & markup
            <small *ngIf="totalAnnotationCount > 0">({{ totalAnnotationCount }})</small>
          </span>
        </label>
        <label class="ss-share-toggle" [class.ss-share-toggle-disabled]="!hasLinkedMeasurements">
          <input type="checkbox" formControlName="includeLinkedMeasurement" [disabled]="!hasLinkedMeasurements" />
          <span>Linked measurement details</span>
        </label>
      </section>

      <!-- Footer with Send -->
      <footer class="ss-share-footer">
        <button type="button" class="ss-share-btn ss-share-btn-ghost" (click)="close()">Cancel</button>
        <button type="button" class="ss-share-btn ss-share-btn-primary" (click)="send()">
          <mat-icon>send</mat-icon>
          <span>Send</span>
        </button>
      </footer>
    </div>

    <!-- Activity tab -->
    <div class="ss-share-body ss-share-body-activity" *ngIf="activeTab === 'activity'">
      <ng-container *ngIf="activityFeed.length > 0; else noActivity">
        <ul class="ss-share-activity-list">
          <li
            class="ss-share-activity-row"
            *ngFor="let item of activityFeed; trackBy: trackByActivity"
          >
            <span class="ss-share-activity-channel" [attr.data-status]="item.record.status">
              <mat-icon>
                {{ item.record.channel === 'email' ? 'mail' :
                   item.record.channel === 'slack' ? 'forum' :
                   item.record.channel === 'teams' ? 'groups' :
                   item.record.channel === 'procore' ? 'business' : 'link' }}
              </mat-icon>
            </span>
            <div class="ss-share-activity-main">
              <span class="ss-share-activity-title">
                <ng-container *ngIf="item.record.recipients.length > 0; else linkLabel">
                  Sent to {{ item.record.recipients.length }} {{ item.record.recipients.length === 1 ? 'person' : 'people' }}
                </ng-container>
                <ng-template #linkLabel>Link created</ng-template>
              </span>
              <span class="ss-share-activity-meta">
                {{ item.screenshot.caption || item.screenshot.pageName }}
                · {{ item.record.expiration }}
                · {{ item.record.viewCount }} view{{ item.record.viewCount === 1 ? '' : 's' }}
              </span>
            </div>
            <span class="ss-share-activity-status" [attr.data-status]="item.record.status">
              {{ item.record.status }}
            </span>
          </li>
        </ul>
      </ng-container>
      <ng-template #noActivity>
        <div class="ss-share-empty">
          <mat-icon>history</mat-icon>
          <p>No share history yet</p>
        </div>
      </ng-template>
    </div>
  </ng-template>
</div>
